import { api } from "./api";
import {
  buildConsultationDomain,
  buildEvaluationFormDomain,
  jsonDomain,
} from "./erpDomain";

/**
 * `patientService` — drop-in replacement for v1's Odoo-backed service that the v1
 * `ConsultationListSection` / `EvaluationFormListSection` used through `useTenant().patientService`.
 *
 * Every fetcher here delegates to the NestJS `mindtalk-doctors-backend` REST API:
 *   /api/v1/:campus/consultations
 *   /api/v1/:campus/sessions/:sessionType
 *   /api/v1/:campus/evaluations
 *   /api/v1/:campus/masters/{employees,doctors,campuses}
 *   /api/v1/patients
 *
 * Output contracts intentionally match v1 so the page components don't have to change:
 *   - List fetchers return `{ data: Row[], total: number }`.
 *   - Doctor list fetchers return `[{ id, name }]`.
 *   - `fetchCampusList()` returns `[{ id, name }]`.
 *   - `getPatients()` returns `[{ id, name }]`.
 *
 * Construct one of these per campus via `createPatientService(campusSlug)`. The TenantContext
 * (re-)constructs it whenever the campus slug changes.
 */

const SESSION_TYPE_PATH = {
  special_educator: "special-educator",
  occupational_therapist: "occupational-therapist",
  speech_therapist: "speech-therapist",
  rehabilitation_psychologist: "rehabilitation-psychologist",
  pediatric_physiotherapist: "pediatric-physiotherapist",
  psychologist: "psychologist",
};

const DOCTOR_DOCTOR_FIELD = {
  psychiatrist: "psychiatrist_id",
  psychologist: "clinical_psychologist_id",
  special_educator: "clinical_psychologist_id",
  occupational_therapist: "clinical_psychologist_id",
  speech_therapist: "clinical_psychologist_id",
  rehabilitation_psychologist: "clinical_psychologist_id",
  pediatric_physiotherapist: "clinical_psychologist_id",
};

function pluckList(payload) {
  if (!payload || typeof payload !== "object") return [];
  for (const key of Object.keys(payload)) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  // A result set of exactly one row comes back as a bare object under the model
  // key instead of a one-element array. Wrap it so the caller sees a list.
  for (const key of Object.keys(payload)) {
    if (key === "pagination") continue;
    const value = payload[key];
    if (value && typeof value === "object") return [value];
  }
  return [];
}

/**
 * `date` is a date-only column, so ordering by it alone leaves same-day rows in an
 * undefined order. Offset paging over a non-deterministic sort makes rows repeat on
 * one page and vanish from another. `id` breaks the tie and makes paging stable.
 */
const LIST_ORDER = "date desc, id desc";

function paginationTotal(payload, fallback) {
  return payload?.pagination?.total_records ?? payload?.pagination?.total ?? fallback;
}

/**
 * The ERP answers a query that matches no rows with HTTP 404 rather than an empty list.
 * Paging past the end of a filtered result set therefore surfaced "Request failed with
 * status code 404" to the user. A 404 from a *list* endpoint means "no rows here", so
 * translate it into an empty page. Any other status still throws.
 */
async function getList(path, params) {
  try {
    const { data } = await api.get(path, { params });
    const rows = pluckList(data);
    return { data: rows, total: paginationTotal(data, rows.length) };
  } catch (e) {
    if (e?.response?.status === 404) return { data: [], total: 0 };
    throw e;
  }
}

/**
 * `pagination.total_records` is returned unchanged whatever `domain` is sent, so it cannot
 * be used to count a filtered result set. Instead ask for every matching row but only the
 * `id` column — the payload stays tiny (500 rows ≈ 6 KB vs ≈ 1 MB with all fields) and the
 * query is fast (worst case measured: 13,932 rows in ~320 ms). The row count *is* the total.
 *
 * Returns `null` if the result set is large enough to have hit COUNT_LIMIT, in which case
 * the caller must fall back to "at least N" paging rather than report a wrong number.
 */
const COUNT_LIMIT = 200000;

async function countList(path, domain) {
  try {
    const { data } = await api.get(path, {
      params: {
        domain: jsonDomain(domain),
        fields: jsonDomain(["id"]),
        limit: COUNT_LIMIT,
        offset: 0,
        page: 1,
        per_page: COUNT_LIMIT,
      },
    });
    const rows = pluckList(data);
    return rows.length >= COUNT_LIMIT ? null : rows.length;
  } catch (e) {
    if (e?.response?.status === 404) return 0;
    throw e;
  }
}

/**
 * Build the standard ERP query string the backend forwards.
 */
function listParams({ domain, page, perPage, order }) {
  const params = {
    domain: jsonDomain(domain),
    limit: perPage,
    offset: (page - 1) * perPage,
    page,
    per_page: perPage,
  };
  if (order) params.order = order;
  return params;
}

async function fetchEmployeesByRole(campus, role) {
  // hr.employee team_role on Mindtalk Odoo. Same pattern v1's apiservice used.
  const params = {
    domain: jsonDomain([["team_role", "=", role]]),
    fields: jsonDomain(["id", "name", "team_role"]),
    limit: 500,
    offset: 0,
  };
  const { data } = await api.get(`/${campus}/masters/employees`, { params });
  return pluckList(data).map((row) => ({
    id: row.id,
    name:
      typeof row.name === "string" && row.name.trim()
        ? row.name.trim()
        : `Employee #${row.id}`,
  }));
}

export function createPatientService(campusSlug) {
  if (!campusSlug) throw new Error("createPatientService: campusSlug is required");
  const campus = campusSlug;

  // -------------------- Doctor / clinician lists --------------------
  async function fetchPsychiatrists() {
    return fetchEmployeesByRole(campus, "psychiatrist");
  }
  async function fetchClinicalPsychologists() {
    return fetchEmployeesByRole(campus, "clinical_psychologist");
  }
  async function fetchSpecialEducators() {
    return fetchEmployeesByRole(campus, "special_educator");
  }
  async function fetchOccupationalTherapists() {
    return fetchEmployeesByRole(campus, "occupational_therapist");
  }
  async function fetchSpeechTherapists() {
    return fetchEmployeesByRole(campus, "speech_therapist");
  }
  async function fetchRehabilitationPsychologists() {
    return fetchEmployeesByRole(campus, "rehabilitation_psychologist");
  }
  async function fetchPediatricPhysiotherapists() {
    return fetchEmployeesByRole(campus, "pediatric_physiotherapist");
  }

  // -------------------- Campus / patient master data --------------------
  async function fetchCampusList() {
    // Mindtalk Odoo populates the `mindtalk.campus` model (subcampus list); other tenants use
    // `res.campus`. Backend exposes two routes: `/:campus/masters/subcampuses` (mindtalk.campus)
    // and `/:campus/masters/campuses` (res.campus). Mirror v1's per-tenant routing.
    const path =
      campus === "mindtalk"
        ? `/${campus}/masters/subcampuses`
        : `/${campus}/masters/campuses`;
    const { data } = await api.get(path, {
      params: {
        fields: jsonDomain(["id", "name", "display_name"]),
        limit: 500,
        offset: 0,
      },
    });
    return pluckList(data).map((row) => {
      let name = "";
      if (typeof row.name === "string" && row.name.trim()) {
        name = row.name.trim();
      } else if (typeof row.display_name === "string") {
        name = row.display_name.split("[")[0]?.trim() || row.display_name;
      }
      return {
        id: row.id,
        name: name || (campus === "mindtalk" ? `Subcampus #${row.id}` : `Campus #${row.id}`),
      };
    });
  }

  /**
   * Mindtalk follow-up types — used by the evaluation-form Follow-up filter. The list is
   * small (a handful of presets like "Psychiatrist", "Psychologist", "Initial assessment")
   * so we fetch up to 200 once and let the UI render a static dropdown.
   */
  async function fetchFollowupTypes() {
    const { data } = await api.get(`/${campus}/masters/followup-types`, {
      params: {
        fields: jsonDomain(["id", "name", "display_name"]),
        limit: 200,
        offset: 0,
      },
    });
    return pluckList(data).map((row) => {
      let name = "";
      if (typeof row.name === "string" && row.name.trim()) {
        name = row.name.trim();
      } else if (typeof row.display_name === "string") {
        name = row.display_name.split("[")[0]?.trim() || row.display_name;
      }
      return { id: row.id, name: name || `Follow-up type #${row.id}` };
    });
  }

  async function getPatients() {
    // Backend mounts patients at `/:campus/patients` (ERP query passthrough → `oeh.medical.patient`).
    // We ask for a wide window so the Patient autocomplete can search locally, mirroring v1.
    const { data } = await api.get(`/${campus}/patients`, {
      params: {
        domain: jsonDomain([]),
        fields: jsonDomain(["id", "name", "display_name"]),
        limit: 1000,
        offset: 0,
      },
    });
    const rows = pluckList(data);
    return rows.map((row) => {
      let name = "";
      if (typeof row.name === "string" && row.name.trim()) {
        name = row.name.trim();
      } else if (typeof row.display_name === "string") {
        name = row.display_name.split("[")[0]?.trim() || row.display_name;
      }
      return { id: row.id, name: name || `Patient #${row.id}` };
    });
  }

  // -------------------- List fetchers (page, perPage, filters) --------------------
  async function fetchPsychiatristConsultationsList(page = 1, perPage = 10, filters = {}) {
    const domain = buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD.psychiatrist);
    return getList(
      `/${campus}/consultations`,
      listParams({ domain, page, perPage, order: LIST_ORDER }),
    );
  }

  async function fetchPsychologistConsultationsList(page = 1, perPage = 10, filters = {}) {
    const domain = buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD.psychologist);
    return getList(
      `/${campus}/sessions/${SESSION_TYPE_PATH.psychologist}`,
      listParams({ domain, page, perPage, order: LIST_ORDER }),
    );
  }

  function makeAlliedListFetcher(kind) {
    const sessionTypePath = SESSION_TYPE_PATH[kind];
    return async function fetcher(page = 1, perPage = 10, filters = {}) {
      const domain = buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD[kind]);
      return getList(
        `/${campus}/sessions/${sessionTypePath}`,
        listParams({ domain, page, perPage, order: LIST_ORDER }),
      );
    };
  }

  const fetchSpecialEducatorSessionsList = makeAlliedListFetcher("special_educator");
  const fetchOccupationalTherapistSessionsList = makeAlliedListFetcher(
    "occupational_therapist",
  );
  const fetchSpeechTherapistSessionsList = makeAlliedListFetcher("speech_therapist");
  const fetchRehabilitationPsychologistSessionsList = makeAlliedListFetcher(
    "rehabilitation_psychologist",
  );
  const fetchPediatricPhysiotherapistSessionsList = makeAlliedListFetcher(
    "pediatric_physiotherapist",
  );

  async function fetchPsychiatristEvaluationFormsList(page = 1, perPage = 10, filters = {}) {
    const domain = buildEvaluationFormDomain(filters);
    return getList(
      `/${campus}/evaluations`,
      listParams({ domain, page, perPage, order: LIST_ORDER }),
    );
  }

  // -------------------- Exact counts for a filtered result set --------------------
  async function countPsychiatristConsultations(filters = {}) {
    return countList(
      `/${campus}/consultations`,
      buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD.psychiatrist),
    );
  }

  async function countPsychologistConsultations(filters = {}) {
    return countList(
      `/${campus}/sessions/${SESSION_TYPE_PATH.psychologist}`,
      buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD.psychologist),
    );
  }

  function makeAlliedCountFetcher(kind) {
    const sessionTypePath = SESSION_TYPE_PATH[kind];
    return async function counter(filters = {}) {
      return countList(
        `/${campus}/sessions/${sessionTypePath}`,
        buildConsultationDomain(filters, DOCTOR_DOCTOR_FIELD[kind]),
      );
    };
  }

  const countSpecialEducatorSessions = makeAlliedCountFetcher("special_educator");
  const countOccupationalTherapistSessions = makeAlliedCountFetcher("occupational_therapist");
  const countSpeechTherapistSessions = makeAlliedCountFetcher("speech_therapist");
  const countRehabilitationPsychologistSessions = makeAlliedCountFetcher(
    "rehabilitation_psychologist",
  );
  const countPediatricPhysiotherapistSessions = makeAlliedCountFetcher(
    "pediatric_physiotherapist",
  );

  async function countPsychiatristEvaluationForms(filters = {}) {
    return countList(`/${campus}/evaluations`, buildEvaluationFormDomain(filters));
  }

  // -------------------- Detail fetchers (for row-click drill-in) --------------------
  async function fetchConsultationById(id) {
    const { data } = await api.get(`/${campus}/consultations`, {
      params: { domain: jsonDomain([["id", "=", Number(id)]]), limit: 1 },
    });
    return pluckList(data)[0] || null;
  }

  async function fetchPsychologistSessionById(id) {
    const { data } = await api.get(`/${campus}/sessions/${SESSION_TYPE_PATH.psychologist}`, {
      params: { domain: jsonDomain([["id", "=", Number(id)]]), limit: 1 },
    });
    return pluckList(data)[0] || null;
  }

  async function fetchOdooSessionRecordById(_objectKey, id) {
    // Backend already routes by session type; we accept the v1 objectKey arg for compatibility
    // but ignore it — the calling code passes us the kind via the URL above.
    return fetchPsychologistSessionById(id);
  }

  async function fetchPsychiatristEvaluationFormById(id) {
    const { data } = await api.get(`/${campus}/evaluations`, {
      params: { domain: jsonDomain([["id", "=", Number(id)]]), limit: 1 },
    });
    return pluckList(data)[0] || null;
  }

  return {
    // doctor lists
    fetchPsychiatrists,
    fetchClinicalPsychologists,
    fetchSpecialEducators,
    fetchOccupationalTherapists,
    fetchSpeechTherapists,
    fetchRehabilitationPsychologists,
    fetchPediatricPhysiotherapists,
    // master data
    fetchCampusList,
    fetchFollowupTypes,
    getPatients,
    // list fetchers
    fetchPsychiatristConsultationsList,
    fetchPsychologistConsultationsList,
    fetchSpecialEducatorSessionsList,
    fetchOccupationalTherapistSessionsList,
    fetchSpeechTherapistSessionsList,
    fetchRehabilitationPsychologistSessionsList,
    fetchPediatricPhysiotherapistSessionsList,
    fetchPsychiatristEvaluationFormsList,
    // exact counts (filtered result sets)
    countPsychiatristConsultations,
    countPsychologistConsultations,
    countSpecialEducatorSessions,
    countOccupationalTherapistSessions,
    countSpeechTherapistSessions,
    countRehabilitationPsychologistSessions,
    countPediatricPhysiotherapistSessions,
    countPsychiatristEvaluationForms,
    // detail
    fetchConsultationById,
    fetchPsychologistSessionById,
    fetchOdooSessionRecordById,
    fetchPsychiatristEvaluationFormById,
  };
}
